import React, { lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { TestModeProvider } from './contexts/TestModeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import HomePage from './components/HomePage';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import { AdminRoute } from './components/routing/AdminRoute';
import { RoutePage } from './components/routing/RoutePage';
// Preserve the existing early Stripe.js load for fraud detection while payment UI loads on demand.
import '@stripe/stripe-js';
import './App.css';

// Import pages directly: the components barrel also exports unrelated routes.
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./components/TermsOfService').then(module => ({ default: module.TermsOfService })));
const Support = lazy(() => import('./components/Support').then(module => ({ default: module.Support })));
const Login = lazy(() => import('./components/Login').then(module => ({ default: module.Login })));
const Signup = lazy(() => import('./components/Signup').then(module => ({ default: module.Signup })));
const DeleteAccountPage = lazy(() => import('./components/DeleteAccountPage').then(module => ({ default: module.DeleteAccountPage })));
const WalletPage = lazy(() => import('./components/wallet/WalletPage').then(module => ({ default: module.WalletPage })));
const TransactionsPage = lazy(() => import('./components/wallet/TransactionsPage').then(module => ({ default: module.TransactionsPage })));
const AffiliatePage = lazy(() => import('./components/affiliate/AffiliatePage').then(module => ({ default: module.AffiliatePage })));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout').then(module => ({ default: module.AdminLayout })));
const AdminUsersPage = lazy(() => import('./components/admin/AdminUsersPage').then(module => ({ default: module.AdminUsersPage })));
const AdminPackagesPage = lazy(() => import('./components/admin/AdminPackagesPage').then(module => ({ default: module.AdminPackagesPage })));
const AdminRedemptionsPage = lazy(() => import('./components/admin/AdminRedemptionsPage').then(module => ({ default: module.AdminRedemptionsPage })));
const AdminRevenuePage = lazy(() => import('./components/admin/AdminRevenuePage').then(module => ({ default: module.AdminRevenuePage })));
const AdminPromoCodesPage = lazy(() => import('./components/admin/AdminPromoCodesPage').then(module => ({ default: module.AdminPromoCodesPage })));
const AdminMediaPage = lazy(() => import('./components/admin/AdminMediaPage').then(module => ({ default: module.AdminMediaPage })));
const AdminWebsiteHeroPage = lazy(() => import('./components/admin/AdminWebsiteHeroPage').then(module => ({ default: module.AdminWebsiteHeroPage })));
const AdminAffiliatesPage = lazy(() => import('./components/admin/AdminAffiliatesPage').then(module => ({ default: module.AdminAffiliatesPage })));
const AdminTokenPricingPage = lazy(() => import('./components/admin/AdminTokenPricingPage').then(module => ({ default: module.AdminTokenPricingPage })));
const AdminSchedulePage = lazy(() => import('./components/admin/AdminSchedulePage').then(module => ({ default: module.AdminSchedulePage })));
const AdminCommunicationsPage = lazy(() => import('./components/admin/AdminCommunicationsPage').then(module => ({ default: module.AdminCommunicationsPage })));
const AdminEventTemplatesPage = lazy(() => import('./components/admin/AdminEventTemplatesPage').then(module => ({ default: module.AdminEventTemplatesPage })));
const AdminLanguagesPage = lazy(() => import('./components/admin/AdminLanguagesPage').then(module => ({ default: module.AdminLanguagesPage })));
const AdminOnboardingPage = lazy(() => import('./components/admin/AdminOnboardingPage').then(module => ({ default: module.AdminOnboardingPage })));

/** Global React Query client with default stale time and retry settings. */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Scrolls the window to the top on every route change. */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
};

export function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<RoutePage><PrivacyPolicy /></RoutePage>} />
        <Route path="/privacypolicy" element={<RoutePage><PrivacyPolicy /></RoutePage>} />
        <Route path="/terms" element={<RoutePage><TermsOfService /></RoutePage>} />
        <Route path="/termsofservices" element={<RoutePage><TermsOfService /></RoutePage>} />
        <Route path="/support" element={<RoutePage><Support /></RoutePage>} />
        <Route path="/login" element={<RoutePage><Login /></RoutePage>} />
        <Route path="/signup" element={<RoutePage><Signup /></RoutePage>} />
        <Route path="/deleteAccount" element={<RoutePage><DeleteAccountPage /></RoutePage>} />
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <RoutePage><WalletPage /></RoutePage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet/transactions"
          element={
            <ProtectedRoute>
              <RoutePage><TransactionsPage /></RoutePage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate"
          element={
            <ProtectedRoute>
              <RoutePage><AffiliatePage /></RoutePage>
            </ProtectedRoute>
          }
        />
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <RoutePage><AdminLayout /></RoutePage>
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<RoutePage><AdminUsersPage /></RoutePage>} />
          <Route path="packages" element={<RoutePage><AdminPackagesPage /></RoutePage>} />
          <Route path="redemptions" element={<RoutePage><AdminRedemptionsPage /></RoutePage>} />
          <Route path="revenue" element={<RoutePage><AdminRevenuePage /></RoutePage>} />
          <Route path="promo-codes" element={<RoutePage><AdminPromoCodesPage /></RoutePage>} />
          <Route path="media" element={<RoutePage><AdminMediaPage /></RoutePage>} />
          <Route path="website-hero" element={<RoutePage><AdminWebsiteHeroPage /></RoutePage>} />
          <Route path="affiliates" element={<RoutePage><AdminAffiliatesPage /></RoutePage>} />
          <Route path="token-pricing" element={<RoutePage><AdminTokenPricingPage /></RoutePage>} />
          <Route path="schedule" element={<RoutePage><AdminSchedulePage /></RoutePage>} />
          <Route path="communications" element={<RoutePage><AdminCommunicationsPage /></RoutePage>} />
          <Route path="events" element={<RoutePage><AdminEventTemplatesPage /></RoutePage>} />
          <Route path="languages" element={<RoutePage><AdminLanguagesPage /></RoutePage>} />
          <Route path="onboarding" element={<RoutePage><AdminOnboardingPage /></RoutePage>} />
        </Route>
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TestModeProvider>
            <CurrencyProvider>
              <AppRoutes />
            </CurrencyProvider>
          </TestModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
